import { FloatingFocusManager, FloatingPortal } from "@floating-ui/react";
import { DialogTitle } from "@headlessui/react";
import {
  FloatingLayerProvider,
  TabbedDrawer,
  zFloating,
} from "@sellar-io/components";
import { twMerge } from "tailwind-merge";
import { useId, type ComponentPropsWithRef, type ReactNode } from "react";
import { useMediaQuery } from "usehooks-ts";
import { PopoverContent } from "../../../components/popover/PopoverContent";
import { PopoverContext, usePopover } from "../../../hooks/usePopover";
import {
  NavigationOverlayPresentationContext,
  useNavigationOverlay,
} from "./NavigationOverlayContext.shared";

export const NavigationOverlay = ({
  id,
  title,
  trigger,
  mobilePresentation = "popover",
  children,
  contentClassName,
}: {
  id: string;
  title: string;
  trigger: (props: ComponentPropsWithRef<"button">) => ReactNode;
  mobilePresentation?: "popover" | "panel";
  children: ReactNode;
  contentClassName?: string;
}) => {
  const { activeOverlay, openOverlay, closeOverlay } = useNavigationOverlay();
  const open = activeOverlay === id;
  const panelId = useId();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const showPanel = isMobile && mobilePresentation === "panel";
  const popover = usePopover({
    open: open && !showPanel,
    onOpenChange: (nextOpen) => {
      if (!nextOpen) {
        closeOverlay(id);
      }
    },
    placement: "bottom-end",
    trigger: "click",
  });

  return (
    <FloatingLayerProvider host="navigationBar">
      <PopoverContext.Provider value={popover}>
        <NavigationOverlayPresentationContext.Provider
          value={showPanel ? "panel" : "popover"}
        >
          {trigger({
            ...popover.getReferenceProps({
              onClick: () => {
                if (open) {
                  closeOverlay(id);
                } else {
                  openOverlay(id);
                }
              },
            }),
            ref: popover.refs.setReference,
            "aria-haspopup": "dialog",
            "aria-expanded": open,
            "aria-controls": open ? panelId : undefined,
          })}
          {showPanel ? (
            <TabbedDrawer
              active={open}
              setActive={(nextOpen) => {
                if (!nextOpen) {
                  closeOverlay(id);
                }
              }}
              hideCloseIcon
              containerClassName="p-0"
              panelClassName="h-dvh w-full max-w-full rounded-none bg-white text-zinc-800"
            >
              <DialogTitle className="sr-only">{title}</DialogTitle>
              <div id={panelId} className="flex min-h-0 flex-1 flex-col">
                {children}
              </div>
            </TabbedDrawer>
          ) : (
            open && (
              <FloatingPortal>
                <FloatingFocusManager
                  context={popover.context}
                  modal={false}
                  initialFocus={popover.refs.floating}
                  returnFocus
                >
                  <PopoverContent
                    portal={false}
                    id={panelId}
                    role="dialog"
                    aria-label={title}
                    tabIndex={-1}
                    className={`flex w-[368px] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-xl border-zinc-200 bg-white text-sm shadow-[2px_2px_16px_0_#0000000f] outline-none ${zFloating.navigationBar.popover}`}
                  >
                    <div
                      className={twMerge(
                        "flex max-h-[480px] min-h-0 flex-col gap-2 p-4",
                        contentClassName,
                      )}
                    >
                      {children}
                    </div>
                  </PopoverContent>
                </FloatingFocusManager>
              </FloatingPortal>
            )
          )}
        </NavigationOverlayPresentationContext.Provider>
      </PopoverContext.Provider>
    </FloatingLayerProvider>
  );
};
